import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useSubmitLead } from '@/hooks/useSubmitLead';
import { toast } from 'sonner';
import { useScrollAnimation } from '@/hooks/useScrollAnimation';
import { cn } from '@/lib/utils';

export function ContactSection() {
  const { submitLead, isSubmitting } = useSubmitLead();
  const { ref: leftRef, isVisible: leftVisible } = useScrollAnimation();
  const { ref: rightRef, isVisible: rightVisible } = useScrollAnimation();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: '',
    website: '', // honeypot field
  });

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numbers, max 11 digits
    const value = e.target.value.replace(/\D/g, '').slice(0, 11);
    setFormData({ ...formData, phone: value });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Client-side validation
    if (formData.name.trim().length < 2) {
      toast.error('Nome deve ter pelo menos 2 caracteres');
      return;
    }
    if (!formData.phone.trim() || formData.phone.length < 10) {
      toast.error('WhatsApp deve ter pelo menos 10 dígitos');
      return;
    }
    if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      toast.error('Email inválido');
      return;
    }
    if (!formData.message.trim() || formData.message.trim().length < 10) {
      toast.error('Mensagem deve ter pelo menos 10 caracteres');
      return;
    }

    const result = await submitLead({
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      message: formData.message,
      source: 'homepage_contact',
    });

    if (result.success) {
      toast.success(result.message);
      setFormData({ name: '', phone: '', email: '', message: '', website: '' });
    } else if (result.isRateLimited) {
      toast.warning(result.message);
    } else {
      toast.error(result.message);
    }
  };

  return (
    <section id="contato" className="py-12 md:py-16 lg:py-24 bg-background">
      <div className="container-custom px-4 sm:px-6 lg:px-8">
        <div className="grid items-start gap-8 md:gap-12 lg:grid-cols-2 lg:gap-20">
          {/* Left Content */}
          <div 
            ref={leftRef}
            className={cn(
              "lg:pt-4 opacity-0",
              leftVisible && "animate-slide-in-left"
            )}
            style={{ animationFillMode: 'forwards' }}
          >
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-[42px] font-bold leading-tight text-foreground">
              Vamos encontrar<br />
              o imóvel ideal<br />
              pra você.
            </h2>
            
            {/* Gold Accent Line */}
            <div className="mt-4 md:mt-6 h-1 w-12 md:w-16 bg-primary" />
            
            <p className="mt-4 md:mt-6 text-sm md:text-base text-muted-foreground leading-relaxed max-w-md">
              Me conte o que você procura e eu retorno<br className="hidden lg:block" />
              com opções alinhadas ao seu perfil, com<br className="hidden lg:block" />
              agilidade e orientação completa.
            </p>
          </div>

          {/* Form */}
          <div 
            ref={rightRef}
            className={cn(
              "opacity-0",
              rightVisible && "animate-slide-in-right"
            )}
            style={{ animationFillMode: 'forwards' }}
          >
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Honeypot field - hidden from users */}
              <div className="absolute left-[-9999px]" aria-hidden="true">
                <Input
                  type="text"
                  name="website"
                  tabIndex={-1}
                  autoComplete="off"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                />
              </div>
              
              <Input
                type="text"
                placeholder="Seu nome"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                minLength={2}
                maxLength={80}
                className="h-12 rounded-none border-border bg-background px-4 text-sm placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-foreground transition-all duration-300 focus:scale-[1.01]"
              />
              <Input
                type="tel"
                inputMode="numeric"
                placeholder="WhatsApp (apenas números)"
                value={formData.phone}
                onChange={handlePhoneChange}
                required
                minLength={10}
                maxLength={11}
                className="h-12 rounded-none border-border bg-background px-4 text-sm placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-foreground transition-all duration-300 focus:scale-[1.01]"
              />
              <Input
                type="email"
                placeholder="E-mail"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                maxLength={120}
                className="h-12 rounded-none border-border bg-background px-4 text-sm placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-foreground transition-all duration-300 focus:scale-[1.01]"
              />
              <Textarea
                placeholder="Mensagem"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                required
                minLength={10}
                maxLength={500}
                className="min-h-[120px] rounded-none border-border bg-background px-4 py-3 text-sm resize-none placeholder:text-muted-foreground focus-visible:ring-1 focus-visible:ring-foreground transition-all duration-300 focus:scale-[1.01]"
              />
              <Button 
                type="submit" 
                className="w-full h-12 rounded-none bg-foreground text-background hover:bg-foreground/90 text-sm font-medium transition-all duration-300 hover:scale-[1.02]"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Enviando...' : 'Enviar mensagem'}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}